// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { CustomFieldInput, CustomFieldOutput } from 'thehive-sdk';
import { useLocalMessage, getOptions } from '@Helpers/';
import { CustomFieldForm, CustomFieldValuesForm } from '@Organisms/CustomField/CustomFieldManage/CustomFieldForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';

import './CustomFieldManage.scss';

interface CustomFieldManageProps {
    triggerElem?: ReactElement;
    onFinish: (CustomField: CustomFieldInput) => void;
    data?: CustomFieldOutput;
    customFieldList?: CustomFieldOutput[];
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

const CustomFieldManage = ({
    triggerElem,
    onFinish,
    data,
    customFieldList = [],
    visible,
    onVisibleChange,
}: CustomFieldManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!data,
        fieldsTouchedInit: !!data,
    });

    const onFinishHandler = ({
        type = 'string',
        displayName = '',
        name = '',
        options = '',
        mandatory = false,
        description = '',
        group = 'default',
    }: CustomFieldValuesForm): void => {
        const opt = !!data ? { customFieldId: data?._id || '' } : {};
        const nameProcessed = name?.trim();

        onFinish({
            type,
            name: nameProcessed,
            displayName: displayName?.trim() || nameProcessed,
            options: options ? getOptions(options, type) : [],
            mandatory,
            description: description?.trim(),
            group: group?.trim(),
            ...opt,
        });
    };

    const tradLabelKey = !!data ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`customField.${tradLabelKey}.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <CustomFieldForm
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    defaultValues={data}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                    customFieldList={customFieldList}
                />
            )}
        </THDrawer>
    );
};

export default CustomFieldManage;
